import { getTranslations } from "providers/I18nProvider";

export type TranslationMatch = {
  id: string;
  keys: string[];
  value: string;
  element: Element;
};

/**
 * Collects all translation keys from the translation files
 */
const collectAllKeys = (): string[] => {
  const keys: string[] = [];

  const traverse = (obj: Record<string, unknown>, prefix = "") => {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "string") {
        keys.push(fullPath);
      } else if (typeof value === "object" && value !== null) {
        traverse(value as Record<string, unknown>, fullPath);
      }
    }
  };

  traverse(getTranslations() as unknown as Record<string, unknown>);
  return keys;
};

const allTranslationKeys = collectAllKeys();

/**
 * Normalizes a translation value for matching against DOM text content.
 * Strips HTML tags and normalizes whitespace using browser's DOM parser.
 */
const normalizeForMatching = (value: string): string => {
  const template = document.createElement("template");
  template.innerHTML = value;
  return (template.content.textContent || "").replace(/\s+/g, " ").trim();
};

/**
 * Builds a reverse lookup map: translation value -> list of keys
 * Uses the t function to get current values (including dynamic overrides)
 * Stores both raw and normalized values for matching
 */
export const buildReverseLookup = (
  t: (key: string) => string,
): Map<string, string[]> => {
  const lookup = new Map<string, string[]>();

  for (const key of allTranslationKeys) {
    const value = t(key);
    if (value && value !== key) {
      const existing = lookup.get(value) || [];
      existing.push(key);
      lookup.set(value, existing);

      const normalized = normalizeForMatching(value);
      if (normalized !== value) {
        const normalizedExisting = lookup.get(normalized) || [];
        normalizedExisting.push(key);
        lookup.set(normalized, normalizedExisting);
      }
    }
  }

  return lookup;
};

/**
 * Scans the DOM for elements containing translated text.
 * Returns a list of matches with their translation keys and values.
 */
export const scanDOMForTranslations = (
  reverseLookup: Map<string, string[]>,
): TranslationMatch[] => {
  const foundMatches: TranslationMatch[] = [];
  const seenElements = new Set<Element>();
  let elementIndex = 0;

  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const text = node.textContent?.trim();
        if (!text || text.length < 2) return NodeFilter.FILTER_REJECT;

        const parent = node.parentElement;
        if (!parent || seenElements.has(parent))
          return NodeFilter.FILTER_REJECT;

        const tagName = parent.tagName.toLowerCase();
        if (tagName === "script" || tagName === "style") {
          return NodeFilter.FILTER_REJECT;
        }

        if (parent.closest("[data-translation-exclude]")) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  let node: Node | null;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement!;
    const rawText = parent.textContent?.trim();
    if (!rawText) continue;

    const text = rawText.replace(/\s+/g, " ").trim();

    const keys = reverseLookup.get(text);
    if (keys && keys.length > 0) {
      seenElements.add(parent);
      const rect = parent.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        foundMatches.push({
          id: `element-${elementIndex}`,
          keys,
          value: rawText,
          element: parent,
        });
        elementIndex++;
      }
    }
  }

  return foundMatches;
};
